import { getDefaultCurrency } from './utils/locale';
import { MATRIX_DEFINITIONS, MatrixType } from './lib/url/matrixMapping';

export type PopulationSource = 'manual' | 'automatic';

export interface UndoAction {
  type: 'add' | 'remove' | 'modify';
  entity: 'station' | 'route';
  data: any;
  previousData?: any;
}

export interface Station {
  id: string;
  name: string;
  position: { x: number; y: number };
  isDeleted?: boolean;
  latitude: number | null;
  longitude: number | null;
  population: number;
  population_source: 'manual' | 'automatic';
  circle_population: number | null;
  circle_population_radius: number | null;
  number: number;
  // The following fields are only used for export and are calculated by the app
  annualArrivals?: number;
  annualRevenue?: number;
  revenuePercentage?: number;
  centrality?: number;
}

export interface Route {
  id: string;
  source: string;
  target: string;
  distance: number;
  isDeleted?: boolean;
  landType: 'domestic' | 'customs' | 'no-customs';
  automaticDistance: number | null;
  distanceCalculation: 'manual' | 'automatic';
}

export interface Network {
  stations: Station[];
  routes: Route[];
}

export interface ProjectConfig {
  bond: {
    currency: string;
    interestRate: number;
    duration: number;
  };
  // Using 'trains' (plural) since these settings apply to the fleet of trains in the network,
  // not just a single train. This matches how the settings are used throughout the application.
  trains: {
    seatsPerTrain: number;
    loadFactor: number;
    maxSpeed: number;
    minimumStationDwellTime: number;
    maximumStationDwellTime: number;
    minimumHeadway: number;
  };
  costs: {
    constructionCostPerKm: number;
    farePerKm: number;
    operatingCostPerKm: number;
    businessShareOfSeats: number;
    businessTicketPremium: number;
    discountShareOfSeats: number;
    discountTicketReduction: number;
  };
  calculation: {
    calibrationConstant: number;
    calibrationConstantCustoms: number;
    calibrationConstantNoCustoms: number;
    distanceFrictionFactor: number;
    minimumRouteDistance: number;
    populationRadius: number;
    densityFrictionFactor: number;
  };
}

export interface ExampleMetadata {
  importedAt: number;
  contentHash: string;
}

export interface Project {
  id: string;
  identifier: string;
  name: string;
  lastEdited: number;
  type: 'user' | 'example';
  config: ProjectConfig;
  network: Network;
  matrices?: CalculationResult;
  isDeleted?: boolean;
  sourceFile?: string;
  deletedAt?: number;
  exampleMetadata?: ExampleMetadata;
}

type MatrixData = {
  [K in MatrixType]: K extends 'cities' ? Station[] : number[][];
};

export interface CalculationResult extends MatrixData {}

export const DEFAULT_CONFIG: ProjectConfig = {
  calculation: {
    calibrationConstant: 200000,
    calibrationConstantCustoms: 20000,
    calibrationConstantNoCustoms: 30000,
    densityFrictionFactor: 0.80,
    distanceFrictionFactor: 2.00,
    minimumRouteDistance: 500,
    populationRadius: 30
  },
  bond: {
    duration: 50,
    interestRate: 3.00,
    currency: getDefaultCurrency()
  },
  costs: {
    constructionCostPerKm: 25000000,
    operatingCostPerKm: 0.09,
    farePerKm: 0.15,
    businessShareOfSeats: 20,
    businessTicketPremium: 40,
    discountShareOfSeats: 20,
    discountTicketReduction: 40
  },
  trains: {
    seatsPerTrain: 400,
    loadFactor: 75,
    maxSpeed: 300,
    minimumStationDwellTime: 2,
    maximumStationDwellTime: 5,
    minimumHeadway: 3
  }
}; 